import { useState } from "react";
import { useFgp } from "@/context/FgpContext";
import { PageHeader, EmptyState } from "@/components/PageHeader";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Wallet, Plus, Pencil, Trash2, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ShareDialog } from "@/components/ShareDialog";

const CATEGORIES = ["Venue", "F&B", "Gifts", "Transport", "Design", "Build", "Furnishing", "Entertainment", "Staffing", "Accommodation", "Other"];
const ITEM_STATUS = ["pending", "approved", "paid"];
const CURRENCIES = ["£", "$", "€", "CHF"];

const uid = (p) => `${p}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
const Field = ({ label, children }) => (<div className="space-y-1.5"><Label className="text-slate-700">{label}</Label>{children}</div>);
const money = (cur, n) => `${cur}${(Number(n) || 0).toLocaleString("en-GB", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const sum = (items, key) => items.reduce((s, i) => s + (Number(i[key]) || 0), 0);

// ---- Budget project dialog (create / edit shell) ----
const BudgetDialog = ({ open, onOpenChange, budget }) => {
  const { addBudget, updateBudget } = useFgp();
  const blank = { name: "", occasion: "", currency: "£" };
  const [f, setF] = useState(budget ? { name: budget.name, occasion: budget.occasion, currency: budget.currency } : blank);
  const submit = () => {
    if (!f.name.trim()) return toast.error("Give this budget a name");
    if (budget) { updateBudget(budget.id, f); toast.success("Budget updated"); }
    else { addBudget(f); toast.success("Budget created"); }
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="budget-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{budget ? "Edit budget" : "New budget"}</DialogTitle>
          <DialogDescription>Track spend for an event, project or occasion against a planned budget.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <Field label="Name"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="budget-name-input" placeholder="e.g. Eleanor's 50th Birthday" /></Field>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Field label="Occasion"><Input value={f.occasion} onChange={(e) => setF({ ...f, occasion: e.target.value })} data-testid="budget-occasion-input" placeholder="Birthday celebration" /></Field>
            </div>
            <Field label="Currency">
              <Select value={f.currency} onValueChange={(v) => setF({ ...f, currency: v })}>
                <SelectTrigger data-testid="budget-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="budget-save-btn">{budget ? "Save" : "Create budget"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Line item dialog (add / edit) ----
const ItemDialog = ({ open, onOpenChange, budget, item }) => {
  const { updateBudget } = useFgp();
  const blank = { category: "Venue", vendor: "", budget: "", actual: "", status: "pending", paymentDate: "" };
  const [f, setF] = useState(item ? { ...item, budget: String(item.budget ?? ""), actual: String(item.actual ?? "") } : blank);
  const submit = () => {
    if (!f.vendor.trim()) return toast.error("Vendor / details are required");
    const bud = parseFloat(f.budget); if (isNaN(bud) || bud < 0) return toast.error("Enter a valid budget amount");
    const act = f.actual === "" ? 0 : parseFloat(f.actual); if (isNaN(act) || act < 0) return toast.error("Enter a valid actual amount");
    const next = { ...f, budget: bud, actual: act };
    const items = item
      ? (budget.items || []).map((i) => i.id === item.id ? { ...i, ...next } : i)
      : [...(budget.items || []), { id: uid("bi"), ...next }];
    updateBudget(budget.id, { items });
    toast.success(item ? "Line item updated" : "Line item added");
    onOpenChange(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="budget-item-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">{item ? "Edit line item" : "Add line item"}</DialogTitle>
          <DialogDescription>A single cost line within “{budget?.name}”.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                <SelectTrigger data-testid="item-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
                <SelectTrigger data-testid="item-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ITEM_STATUS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
          </div>
          <Field label="Vendor / details"><Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} data-testid="item-vendor-input" placeholder="e.g. Regent Garden" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={`Budget (${budget?.currency})`}><Input type="number" value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="item-budget-input" placeholder="0" /></Field>
            <Field label={`Actual (${budget?.currency})`}><Input type="number" value={f.actual} onChange={(e) => setF({ ...f, actual: e.target.value })} data-testid="item-actual-input" placeholder="0" /></Field>
          </div>
          <Field label="Payment date"><Input type="date" value={f.paymentDate || ""} onChange={(e) => setF({ ...f, paymentDate: e.target.value })} data-testid="item-payment-input" /></Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} className="bg-neutral-600 hover:bg-neutral-700" data-testid="item-save-btn">{item ? "Save" : "Add item"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const BudgetCard = ({ budget }) => {
  const { updateBudget, deleteBudget } = useFgp();
  const [editBudget, setEditBudget] = useState(false);
  const [itemOpen, setItemOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const items = budget.items || [];
  const totalBudget = sum(items, "budget");
  const totalActual = sum(items, "actual");
  const variance = totalBudget - totalActual;
  const pct = totalBudget > 0 ? Math.min(100, Math.round((totalActual / totalBudget) * 100)) : 0;
  const over = totalActual > totalBudget;

  const openAdd = () => { setEditingItem(null); setItemOpen(true); };
  const openEdit = (it) => { setEditingItem(it); setItemOpen(true); };
  const removeItem = (id) => { updateBudget(budget.id, { items: items.filter((i) => i.id !== id) }); toast.success("Line item removed"); };

  const shareUrl = `${window.location.origin}/app/budgeting?b=${budget.id}`;
  const shareHtml = `
    <div class="totals">
      <div class="box"><div class="lbl">Total budget</div><div class="val">${money(budget.currency, totalBudget)}</div></div>
      <div class="box"><div class="lbl">Total actual</div><div class="val">${money(budget.currency, totalActual)}</div></div>
      <div class="box"><div class="lbl">${variance >= 0 ? "Remaining" : "Over budget"}</div><div class="val" style="color:${over ? "#dc2626" : "#16a34a"}">${money(budget.currency, Math.abs(variance))}</div></div>
    </div>
    <table><thead><tr><th>Category</th><th>Vendor / details</th><th class="num">Budget</th><th class="num">Actual</th><th>Payment date</th><th>Status</th></tr></thead>
    <tbody>${items.map((it) => `<tr><td>${it.category}</td><td>${it.vendor}</td><td class="num">${money(budget.currency, it.budget)}</td><td class="num">${money(budget.currency, it.actual)}</td><td>${it.paymentDate ? new Date(it.paymentDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</td><td><span class="pill">${it.status}</span></td></tr>`).join("")}</tbody></table>`;

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`budget-card-${budget.id}`}>
      <div className="p-5 border-b border-slate-100">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">{budget.name}</h3>
            {budget.occasion && <p className="text-sm text-slate-500">{budget.occasion}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setShareOpen(true)} data-testid={`share-budget-${budget.id}`}><Share2 className="h-3.5 w-3.5" /> Share</Button>
            <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setEditBudget(true)} data-testid={`edit-budget-${budget.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
            <Button variant="outline" size="sm" className="gap-1.5 text-rose-600 hover:text-rose-700" onClick={() => { deleteBudget(budget.id); toast.success("Budget deleted"); }} data-testid={`delete-budget-${budget.id}`}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
            <Button size="sm" className="gap-1.5 bg-neutral-600 hover:bg-neutral-700" onClick={openAdd} data-testid={`add-item-${budget.id}`}><Plus className="h-4 w-4" /> Add item</Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-5">
          <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Budget</p><p className="mt-1 font-heading text-xl font-semibold text-slate-900">{money(budget.currency, totalBudget)}</p></div>
          <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Actual</p><p className="mt-1 font-heading text-xl font-semibold text-slate-900">{money(budget.currency, totalActual)}</p></div>
          <div><p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{variance >= 0 ? "Remaining" : "Over"}</p><p className={`mt-1 font-heading text-xl font-semibold ${over ? "text-rose-600" : "text-emerald-600"}`}>{money(budget.currency, Math.abs(variance))}</p></div>
        </div>
        <div className="mt-4 h-2 rounded-full bg-slate-100 overflow-hidden" data-testid={`budget-progress-${budget.id}`}>
          <div className={`h-full ${over ? "bg-rose-500" : "bg-emerald-500"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>

      {items.length === 0 ? (
        <div className="p-8 text-center">
          <p className="text-sm text-slate-400">No line items yet — add your first cost line.</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50">
              <TableHead>Category</TableHead>
              <TableHead>Vendor / details</TableHead>
              <TableHead className="text-right">Budget</TableHead>
              <TableHead className="text-right">Actual</TableHead>
              <TableHead className="hidden md:table-cell">Payment date</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((it) => (
              <TableRow key={it.id} data-testid={`budget-item-row-${it.id}`}>
                <TableCell><span className="text-sm font-medium text-slate-700">{it.category}</span></TableCell>
                <TableCell className="text-sm text-slate-600">{it.vendor}</TableCell>
                <TableCell className="text-right text-sm text-slate-700">{money(budget.currency, it.budget)}</TableCell>
                <TableCell className="text-right text-sm font-medium text-slate-900">{money(budget.currency, it.actual)}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-slate-400">{it.paymentDate ? new Date(it.paymentDate).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</TableCell>
                <TableCell><StatusBadge status={it.status} /></TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(it)} className="text-slate-400 hover:text-slate-700" data-testid={`edit-item-${it.id}`}><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => removeItem(it.id)} className="text-slate-400 hover:text-rose-600" data-testid={`delete-item-${it.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {editBudget && <BudgetDialog open={editBudget} onOpenChange={setEditBudget} budget={budget} />}
      {itemOpen && <ItemDialog open={itemOpen} onOpenChange={setItemOpen} budget={budget} item={editingItem} />}
      {shareOpen && <ShareDialog open={shareOpen} onOpenChange={setShareOpen} testidPrefix={`share-budget-${budget.id}`}
        docTitle={budget.name} docSubtitle={budget.occasion} bodyHtml={shareHtml} shareUrl={shareUrl} />}
    </motion.div>
  );
};

export default function Budgeting() {
  const { budgets } = useFgp();
  const [open, setOpen] = useState(false);

  const grandBudget = budgets.reduce((s, b) => s + sum(b.items || [], "budget"), 0);
  const grandActual = budgets.reduce((s, b) => s + sum(b.items || [], "actual"), 0);

  return (
    <div>
      <PageHeader title="Budgeting" subtitle="Plan and track spend for events, projects and occasions."
        actions={<Button onClick={() => setOpen(true)} className="bg-neutral-600 hover:bg-neutral-700 gap-2" data-testid="add-budget"><Plus className="h-4 w-4" /> New budget</Button>} />

      {budgets.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Active budgets", value: budgets.length, tone: "text-slate-900" },
            { label: "Total planned", value: money("£", grandBudget), tone: "text-slate-900" },
            { label: "Total actual", value: money("£", grandActual), tone: "text-slate-900" },
            { label: grandBudget - grandActual >= 0 ? "Remaining" : "Over budget", value: money("£", Math.abs(grandBudget - grandActual)), tone: grandActual > grandBudget ? "text-rose-600" : "text-emerald-600" },
          ].map((s) => (
            <div key={s.label} className="bg-white rounded-xl border border-slate-200 p-5">
              <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">{s.label}</p>
              <p className={`mt-2 font-heading text-2xl font-semibold ${s.tone}`}>{s.value}</p>
            </div>
          ))}
        </div>
      )}

      {budgets.length === 0 ? (
        <EmptyState icon={Wallet} title="No budgets yet"
          hint="Create a budget for an event or project, then break it down into line items to track planned vs actual spend."
          testid="budgets-empty" />
      ) : (
        <div className="space-y-6">
          {budgets.map((b) => <BudgetCard key={b.id} budget={b} />)}
        </div>
      )}

      {open && <BudgetDialog open={open} onOpenChange={setOpen} />}
    </div>
  );
}
